import { useState, useCallback, useMemo } from "react";
import {
  useGetList,
  useDataProvider,
  useNotify,
} from "ra-core";
import {
  Phone,
  Plus,
  Trash2,
  Star,
  Loader2,
  Tag,
  PhoneForwarded,
  Mic,
  MessageSquare,
  Search,
  ShoppingCart,
  ChevronDown,
  ChevronRight,
  Settings2,
  Megaphone,
} from "lucide-react";

// ─── Types ──────────────────────────────────────────────────────
interface OrgCampaign {
  id: string;
  org_id: string;
  name: string;
  channel: string;
  status: string;
  notes: string;
  created_at: string;
}

interface OrgPhoneNumber {
  id: string;
  org_id: string;
  phone_number: string;
  label: string;
  campaign: string;
  campaign_id: string | null;
  forward_to_number: string | null;
  voice_enabled: boolean;
  sms_enabled: boolean;
  recording_enabled: boolean;
  is_primary: boolean;
  twilio_sid: string | null;
  created_at: string;
}

interface TwilioSearchResult {
  phone_number: string;
  friendly_name: string;
  region: string;
  locality: string;
}

interface OrgPhoneNumbersProps {
  orgId: string;
}

const CHANNELS = [
  { value: "google_ads", label: "Google Ads" },
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "yard_signs", label: "Yard Signs" },
  { value: "referral", label: "Referral" },
  { value: "website", label: "Website" },
  { value: "direct_mail", label: "Direct Mail" },
  { value: "door_knocking", label: "Door Knocking" },
  { value: "home_advisor", label: "HomeAdvisor" },
  { value: "angi", label: "Angi" },
  { value: "thumbtack", label: "Thumbtack" },
  { value: "nextdoor", label: "Nextdoor" },
  { value: "yelp", label: "Yelp" },
  { value: "radio", label: "Radio" },
  { value: "tv", label: "TV" },
  { value: "print", label: "Print" },
  { value: "other", label: "Other" },
];

// ─── Component ──────────────────────────────────────────────────
export function OrgPhoneNumbers({ orgId }: OrgPhoneNumbersProps) {
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // ── Data ────────────────────
  const {
    data: campaigns,
    isPending: campaignsLoading,
    refetch: refetchCampaigns,
  } = useGetList<OrgCampaign>("org_campaigns", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "ASC" },
    filter: { org_id: orgId },
  });

  const {
    data: numbers,
    isPending: numbersLoading,
    refetch: refetchNumbers,
  } = useGetList<OrgPhoneNumber>("org_phone_numbers", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "ASC" },
    filter: { org_id: orgId },
  });

  const refetchAll = useCallback(() => {
    refetchCampaigns();
    refetchNumbers();
  }, [refetchCampaigns, refetchNumbers]);

  // ── UI State ────────────────
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [newCampName, setNewCampName] = useState("");
  const [newCampChannel, setNewCampChannel] = useState("other");
  const [savingCampaign, setSavingCampaign] = useState(false);

  const [showAddNumber, setShowAddNumber] = useState(false);
  const [addMode, setAddMode] = useState<"manual" | "search">("manual");
  const [addCampaignId, setAddCampaignId] = useState<string>("");
  const [addLabel, setAddLabel] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [addForward, setAddForward] = useState("");
  const [addVoice, setAddVoice] = useState(true);
  const [addSms, setAddSms] = useState(true);
  const [addRecording, setAddRecording] = useState(true);
  const [addingNumber, setAddingNumber] = useState(false);

  // Search state
  const [searchAreaCode, setSearchAreaCode] = useState("");
  const [searchType, setSearchType] = useState<"local" | "toll_free">("local");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<TwilioSearchResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState(false);

  const [expandedCampaign, setExpandedCampaign] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null);

  // Group numbers by campaign
  const unassignedNumbers = useMemo(
    () => (numbers || []).filter((n) => !n.campaign_id),
    [numbers],
  );

  const numbersByCampaign = useMemo(() => {
    const map: Record<string, OrgPhoneNumber[]> = {};
    for (const n of numbers || []) {
      if (n.campaign_id) {
        if (!map[n.campaign_id]) map[n.campaign_id] = [];
        map[n.campaign_id].push(n);
      }
    }
    return map;
  }, [numbers]);

  // ── Helpers ─────────────────
  const getToken = useCallback(() => {
    try {
      const raw = localStorage.getItem("supabase.auth.token") || "{}";
      const parsed = JSON.parse(raw);
      return parsed?.currentSession?.access_token || parsed?.access_token || null;
    } catch {
      return null;
    }
  }, []);

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";

  // ── Campaign CRUD ───────────
  const handleCreateCampaign = useCallback(async () => {
    if (!newCampName.trim()) return;
    setSavingCampaign(true);
    try {
      await dataProvider.create("org_campaigns", {
        data: {
          org_id: orgId,
          name: newCampName.trim(),
          channel: newCampChannel,
        },
      });
      notify("Campaign created", { type: "success" });
      setNewCampName("");
      setNewCampChannel("other");
      setShowNewCampaign(false);
      refetchAll();
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.includes("duplicate") || msg.includes("unique")) {
        notify("A campaign with this name already exists", { type: "error" });
      } else {
        notify(`Failed: ${msg}`, { type: "error" });
      }
    } finally {
      setSavingCampaign(false);
    }
  }, [newCampName, newCampChannel, orgId, dataProvider, notify, refetchAll]);

  const handleDeleteCampaign = useCallback(
    async (id: string) => {
      if (!confirm("Delete this campaign? Phone numbers will become unassigned.")) return;
      try {
        await dataProvider.delete("org_campaigns", { id, previousData: { id } });
        notify("Campaign deleted", { type: "success" });
        refetchAll();
      } catch (err) {
        notify(`Failed: ${(err as Error).message}`, { type: "error" });
      }
    },
    [dataProvider, notify, refetchAll],
  );

  // ── Number CRUD ─────────────
  const resetAddForm = useCallback(() => {
    setAddPhone("");
    setAddLabel("");
    setAddForward("");
    setAddCampaignId("");
    setAddVoice(true);
    setAddSms(true);
    setAddRecording(true);
    setSearchResults([]);
    setSelectedResult(null);
    setShowAddNumber(false);
    setAddMode("manual");
  }, []);

  const handleAddManual = useCallback(async () => {
    const phone = addPhone.trim();
    if (!phone) return;
    const normalized = phone.startsWith("+") ? phone : `+1${phone.replace(/\D/g, "")}`;
    if (!/^\+[1-9]\d{9,14}$/.test(normalized)) {
      notify("Enter a valid E.164 phone number (e.g. +15551234567)", { type: "warning" });
      return;
    }
    setAddingNumber(true);
    try {
      const isPrimary = !numbers || numbers.length === 0;
      await dataProvider.create("org_phone_numbers", {
        data: {
          org_id: orgId,
          phone_number: normalized,
          label: addLabel.trim() || "Main",
          campaign_id: addCampaignId || null,
          forward_to_number: addForward.trim() || null,
          voice_enabled: addVoice,
          sms_enabled: addSms,
          recording_enabled: addRecording,
          is_primary: isPrimary,
        },
      });
      notify("Number added", { type: "success" });
      resetAddForm();
      refetchAll();
    } catch (err) {
      const msg = (err as Error).message;
      if (msg.includes("duplicate") || msg.includes("unique")) {
        notify("This number is already registered", { type: "error" });
      } else {
        notify(`Failed: ${msg}`, { type: "error" });
      }
    } finally {
      setAddingNumber(false);
    }
  }, [addPhone, addLabel, addCampaignId, addForward, addVoice, addSms, addRecording, numbers, orgId, dataProvider, notify, resetAddForm, refetchAll]);

  // ── Twilio Search & Buy ─────
  const handleSearch = useCallback(async () => {
    const token = getToken();
    if (!token) { notify("Auth token missing — please refresh", { type: "error" }); return; }
    setSearching(true);
    setSearchResults([]);
    try {
      const resp = await fetch(`${supabaseUrl}/functions/v1/twilio-number-search`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          area_code: searchAreaCode.trim() || undefined,
          type: searchType,
          limit: 10,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Search failed");
      setSearchResults(data.numbers || []);
      if ((data.numbers || []).length === 0) {
        notify("No numbers available for that search. Try a different area code.", { type: "info" });
      }
    } catch (err) {
      notify(`Search failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setSearching(false);
    }
  }, [searchAreaCode, searchType, supabaseUrl, getToken, notify]);

  const handlePurchase = useCallback(async () => {
    if (!selectedResult) return;
    const token = getToken();
    if (!token) { notify("Auth token missing", { type: "error" }); return; }
    setPurchasing(true);
    try {
      const resp = await fetch(`${supabaseUrl}/functions/v1/twilio-number-purchase`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          phone_number: selectedResult,
          label: addLabel.trim() || "Campaign",
          campaign_id: addCampaignId || null,
          forward_to_number: addForward.trim() || null,
          voice_enabled: addVoice,
          sms_enabled: addSms,
          recording_enabled: addRecording,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Purchase failed");
      notify(`Number ${selectedResult} purchased and configured!`, { type: "success" });
      resetAddForm();
      refetchAll();
    } catch (err) {
      notify(`Purchase failed: ${(err as Error).message}`, { type: "error" });
    } finally {
      setPurchasing(false);
    }
  }, [selectedResult, addLabel, addCampaignId, addForward, addVoice, addSms, addRecording, supabaseUrl, getToken, notify, resetAddForm, refetchAll]);

  // ── Primary / Delete ────────
  const handleSetPrimary = useCallback(
    async (id: string) => {
      if (!numbers) return;
      setSettingPrimaryId(id);
      try {
        const current = numbers.find((n) => n.is_primary);
        if (current && current.id !== id) {
          await dataProvider.update("org_phone_numbers", {
            id: current.id,
            data: { is_primary: false },
            previousData: current,
          });
        }
        const target = numbers.find((n) => n.id === id);
        if (target) {
          await dataProvider.update("org_phone_numbers", {
            id,
            data: { is_primary: true },
            previousData: target,
          });
        }
        notify("Primary number updated", { type: "success" });
        refetchAll();
      } catch (err) {
        notify(`Failed: ${(err as Error).message}`, { type: "error" });
      } finally {
        setSettingPrimaryId(null);
      }
    },
    [numbers, dataProvider, notify, refetchAll],
  );

  const handleDeleteNumber = useCallback(
    async (id: string) => {
      if (!confirm("Remove this phone number?")) return;
      setDeletingId(id);
      try {
        await dataProvider.delete("org_phone_numbers", { id, previousData: { id } });
        notify("Number removed", { type: "success" });
        refetchAll();
      } catch (err) {
        notify(`Failed: ${(err as Error).message}`, { type: "error" });
      } finally {
        setDeletingId(null);
      }
    },
    [dataProvider, notify, refetchAll],
  );

  // ── Render helpers ──────────
  const renderNumberRow = (num: OrgPhoneNumber) => (
    <div
      key={num.id}
      className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors border-b last:border-b-0"
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm font-medium">{num.phone_number}</span>
          {num.is_primary && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-700">
              <Star className="w-2.5 h-2.5 fill-current" />Primary
            </span>
          )}
          {num.label && (
            <span className="text-xs text-muted-foreground">{num.label}</span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 flex-wrap">
          {num.forward_to_number && (
            <span className="inline-flex items-center gap-1 text-[10px] text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
              <PhoneForwarded className="w-2.5 h-2.5" />
              → {num.forward_to_number}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
            style={{ color: num.voice_enabled ? "#15803d" : "#9ca3af", backgroundColor: num.voice_enabled ? "#f0fdf4" : "#f9fafb" }}>
            <Phone className="w-2.5 h-2.5" />Voice {num.voice_enabled ? "ON" : "OFF"}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
            style={{ color: num.sms_enabled ? "#15803d" : "#9ca3af", backgroundColor: num.sms_enabled ? "#f0fdf4" : "#f9fafb" }}>
            <MessageSquare className="w-2.5 h-2.5" />SMS {num.sms_enabled ? "ON" : "OFF"}
          </span>
          {num.voice_enabled && (
            <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
              style={{ color: num.recording_enabled ? "#15803d" : "#9ca3af", backgroundColor: num.recording_enabled ? "#f0fdf4" : "#f9fafb" }}>
              <Mic className="w-2.5 h-2.5" />Rec {num.recording_enabled ? "ON" : "OFF"}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 ml-3">
        {!num.is_primary && (
          <button onClick={() => handleSetPrimary(num.id)} disabled={settingPrimaryId === num.id}
            className="p-1.5 rounded-md text-muted-foreground hover:text-amber-600 hover:bg-amber-50 transition-colors" title="Set as primary outbound">
            {settingPrimaryId === num.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Star className="w-3.5 h-3.5" />}
          </button>
        )}
        <button onClick={() => handleDeleteNumber(num.id)} disabled={deletingId === num.id}
          className="p-1.5 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors" title="Remove">
          {deletingId === num.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );

  const isPending = campaignsLoading || numbersLoading;

  // ─── Render ─────────────────────────────────────────
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Megaphone className="w-5 h-5" />
          Campaigns & Phone Numbers
        </h2>
        <div className="flex gap-2">
          <button onClick={() => setShowNewCampaign(!showNewCampaign)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 transition-colors">
            <Tag className="w-3.5 h-3.5" />New campaign
          </button>
          <button onClick={() => setShowAddNumber(!showAddNumber)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors">
            <Plus className="w-3.5 h-3.5" />Add number
          </button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Each campaign can have one or more Twilio tracking numbers. Inbound calls and SMS are attributed to the campaign for ROI tracking. The <strong>primary</strong> number is used for outbound SMS and estimate delivery.
      </p>

      {/* ── New Campaign Form ────────────── */}
      {showNewCampaign && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3 border">
          <h3 className="text-sm font-medium">Create Campaign</h3>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium mb-1 block">Name</span>
              <input type="text" value={newCampName} onChange={(e) => setNewCampName(e.target.value)}
                placeholder="e.g. Google Ads Q1" autoFocus
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </label>
            <label className="block">
              <span className="text-xs font-medium mb-1 block">Channel</span>
              <select value={newCampChannel} onChange={(e) => setNewCampChannel(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                {CHANNELS.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setShowNewCampaign(false); setNewCampName(""); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium border text-gray-700 hover:bg-gray-100">Cancel</button>
            <button onClick={handleCreateCampaign} disabled={savingCampaign || !newCampName.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
              {savingCampaign ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
              {savingCampaign ? "Creating…" : "Create"}
            </button>
          </div>
        </div>
      )}

      {/* ── Add Number Form ──────────────── */}
      {showAddNumber && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-3 border">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">Add Phone Number</h3>
            <div className="flex rounded-lg border overflow-hidden">
              <button onClick={() => { setAddMode("manual"); setSearchResults([]); setSelectedResult(null); }}
                className={`px-3 py-1 text-xs font-medium transition-colors ${addMode === "manual" ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                Manual
              </button>
              <button onClick={() => setAddMode("search")}
                className={`px-3 py-1 text-xs font-medium transition-colors ${addMode === "search" ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}>
                Search & Buy
              </button>
            </div>
          </div>

          {/* Common fields */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium mb-1 block">Label</span>
              <input type="text" value={addLabel} onChange={(e) => setAddLabel(e.target.value)}
                placeholder="e.g. Google Ads"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </label>
            <label className="block">
              <span className="text-xs font-medium mb-1 block">Campaign</span>
              <select value={addCampaignId} onChange={(e) => setAddCampaignId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                <option value="">No campaign</option>
                {(campaigns || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="text-xs font-medium mb-1 flex items-center gap-1">
              <PhoneForwarded className="w-3 h-3" />Forward calls to
            </span>
            <input type="tel" value={addForward} onChange={(e) => setAddForward(e.target.value)}
              placeholder="+14045551234 (contractor cell/office)"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span className="text-[10px] text-muted-foreground mt-0.5 block">Required for voice calls. Leave blank for SMS-only numbers.</span>
          </label>

          {/* Toggles */}
          <div className="flex gap-4 items-center">
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={addVoice} onChange={(e) => setAddVoice(e.target.checked)} className="rounded" />
              <Phone className="w-3 h-3" />Voice
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={addSms} onChange={(e) => setAddSms(e.target.checked)} className="rounded" />
              <MessageSquare className="w-3 h-3" />SMS
            </label>
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" checked={addRecording} onChange={(e) => setAddRecording(e.target.checked)} className="rounded" />
              <Mic className="w-3 h-3" />Record calls
            </label>
          </div>

          {/* Manual entry */}
          {addMode === "manual" && (
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-medium mb-1 block">Phone number (E.164)</span>
                <input type="tel" value={addPhone} onChange={(e) => setAddPhone(e.target.value)}
                  placeholder="+15551234567" autoFocus
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </label>
              <div className="flex gap-2 justify-end">
                <button onClick={resetAddForm}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border text-gray-700 hover:bg-gray-100">Cancel</button>
                <button onClick={handleAddManual} disabled={addingNumber || !addPhone.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
                  {addingNumber ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  {addingNumber ? "Adding…" : "Add number"}
                </button>
              </div>
            </div>
          )}

          {/* Search & Buy */}
          {addMode === "search" && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input type="text" value={searchAreaCode} onChange={(e) => setSearchAreaCode(e.target.value)}
                  placeholder="Area code (e.g. 404)" maxLength={3}
                  className="w-32 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <select value={searchType} onChange={(e) => setSearchType(e.target.value as "local" | "toll_free")}
                  className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                  <option value="local">Local</option>
                  <option value="toll_free">Toll-Free</option>
                </select>
                <button onClick={handleSearch} disabled={searching}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
                  {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  {searching ? "Searching…" : "Search"}
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className="border rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                  {searchResults.map((r) => (
                    <button key={r.phone_number} onClick={() => setSelectedResult(r.phone_number)}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm border-b last:border-b-0 transition-colors ${
                        selectedResult === r.phone_number ? "bg-blue-50 border-blue-200" : "hover:bg-gray-50"
                      }`}>
                      <span className="font-mono font-medium">{r.friendly_name || r.phone_number}</span>
                      <span className="text-xs text-muted-foreground">{r.locality}{r.region ? `, ${r.region}` : ""}</span>
                    </button>
                  ))}
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <button onClick={resetAddForm}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border text-gray-700 hover:bg-gray-100">Cancel</button>
                <button onClick={handlePurchase} disabled={purchasing || !selectedResult}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">
                  {purchasing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5" />}
                  {purchasing ? "Purchasing…" : "Buy & add"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {isPending && (
        <div className="py-4 text-center text-sm text-muted-foreground">Loading…</div>
      )}

      {/* ── Campaign groups ──────────────── */}
      {!isPending && campaigns && campaigns.length > 0 && (
        <div className="space-y-3">
          {campaigns.map((camp) => {
            const campNumbers = numbersByCampaign[camp.id] || [];
            const isExpanded = expandedCampaign === camp.id;
            return (
              <div key={camp.id} className="border rounded-lg overflow-hidden">
                <button onClick={() => setExpandedCampaign(isExpanded ? null : camp.id)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                  <div className="flex items-center gap-2">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    <Megaphone className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium">{camp.name}</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">
                      {CHANNELS.find((c) => c.value === camp.channel)?.label || camp.channel}
                    </span>
                    <span className="text-[10px] text-muted-foreground">{campNumbers.length} number{campNumbers.length !== 1 ? "s" : ""}</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handleDeleteCampaign(camp.id); }}
                    className="p-1 rounded hover:bg-red-50 hover:text-red-600 text-muted-foreground transition-colors" title="Delete campaign">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </button>
                {isExpanded && (
                  <div className="border-t">
                    {campNumbers.length === 0 ? (
                      <div className="px-4 py-3 text-xs text-muted-foreground">No numbers assigned to this campaign yet.</div>
                    ) : (
                      campNumbers.map(renderNumberRow)
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Unassigned numbers ───────────── */}
      {!isPending && unassignedNumbers.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1">
            {campaigns && campaigns.length > 0 ? "Unassigned Numbers" : "Phone Numbers"}
          </h3>
          <div className="border rounded-lg overflow-hidden">
            {unassignedNumbers.map(renderNumberRow)}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isPending && (!numbers || numbers.length === 0) && (!campaigns || campaigns.length === 0) && !showAddNumber && !showNewCampaign && (
        <div className="py-8 text-center">
          <Megaphone className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-40" />
          <p className="text-sm text-muted-foreground">No campaigns or phone numbers configured.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Create a campaign and add a Twilio number to start tracking leads.
          </p>
        </div>
      )}
    </div>
  );
}
